// Outlook Integration API
import * as constants from "../constants/CONSTANT";

class OutlookApi {

  constructor() {
    this.baseUrl = `${constants.API_BASE_URL}/api/outlook`;
  }
  async makeBackendRequest(endpoint, options = {}, retries = 3) {
    const token = localStorage.getItem('token');
    console.log("Token outlook", token)
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    };
    const finalOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers
      }
    };

    let lastError;

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        const response = await fetch(`${this.baseUrl}${endpoint}`, finalOptions);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Request failed' }));


          if (response.status >= 500 || response.status === 429 || response.status === 504) {
            lastError = new Error(errorData.error || `Request failed: ${response.status}`);

            if (attempt < retries) {
              const delay = 1500 * attempt;
              console.log(` Outlook API error (${response.status}), retrying in ${delay}ms... (attempt ${attempt}/${retries})`);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
          }

          throw new Error(errorData.error || `Request failed: ${response.status}`);
        }

        return await response.json();

      } catch (error) {
        lastError = error;

        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          if (attempt < retries) {
            const delay = 1500 * attempt;
            console.log(` Network error, retrying in ${delay}ms... (attempt ${attempt}/${retries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }
        }

        throw error;
      }
    }

    throw lastError;
  }
  /**
   * Get all calendars for the user
   */
  async getAllCalendars() {
    try {
      const response = await this.makeBackendRequest('/calendars');
      return response.calendars || [];
    } catch (error) {
      throw error;
    }
  }
  /**
   * Get events from a specific calendar
   */
  async getCalendarEvents(calendarId = null, startDate = null, endDate = null) {
    try {
      let queryParams = new URLSearchParams();

      if (calendarId) {
        queryParams.append('calendarId', calendarId);
      }
      if (startDate) {
        queryParams.append('startDate', startDate);
      }
      if (endDate) {
        queryParams.append('endDate', endDate);
      }

      // ✅ Add cache-busting timestamp to force fresh data
      queryParams.append('_t', Date.now());

      const endpoint = queryParams.toString()
        ? `/events?${queryParams.toString()}`
        : '/events';

      const response = await this.makeBackendRequest(endpoint, {
        // ✅ Disable caching
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      console.log(' Outlook events response:', response);
      return response.events || [];
    } catch (error) {
      console.error(' Error fetching Outlook events:', error);
      throw error;
    }
  }

  /**
   * Create a new event in Outlook calendar
   */
  async createEvent(eventData) {
    try {
      const response = await this.makeBackendRequest('/events', {
        method: 'POST',
        body: JSON.stringify(eventData)
      });
      return response.event;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update an existing event
   */
  async updateEvent(eventId, eventData) {
    try {
      const response = await this.makeBackendRequest(`/events/${eventId}`, {
        method: 'PATCH',
        body: JSON.stringify(eventData)
      });
      return response.event;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete an event
   */
  async deleteEvent(eventId) {
    try {
      const response = await this.makeBackendRequest(`/events/${eventId}`, {
        method: 'DELETE'
      });
      return response.success;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Sync Spark CRM events with Outlook
   */
  syncWithOutlook(sparkEvents) {
    try {


      const outlookEvents = [];

      for (const sparkEvent of sparkEvents) {
        try {
          const outlookEvent = this.createEvent({
            subject: sparkEvent.title,
            body: {
              contentType: "HTML",
              content: sparkEvent.description || "Event synced from Spark CRM"
            },
            start: {
              dateTime: sparkEvent.start,
              timeZone: "Asia/Kolkata"
            },
            end: {
              dateTime: sparkEvent.end,
              timeZone: "Asia/Kolkata"
            },
            location: {
              displayName: sparkEvent.location || "Office"
            },
            attendees: sparkEvent.guests ? sparkEvent.guests.split(',').map(email => ({
              emailAddress: {
                address: email.trim()
              },
              type: "required"
            })) : []
          });

          outlookEvents.push(outlookEvent);
        } catch (error) {

        }
      }

      return outlookEvents;
    } catch (error) {

      throw error;
    }
  }

  /**
   * Get user information from Outlook
   */
  async getUserInfo() {
    try {
      const response = await this.makeBackendRequest('/user');
      return response;
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Failed to fetch user info'
      };
    }
  }

  async getOutlookAccounts() {
    try {
      const response = await this.makeBackendRequest('/accounts');
      return response;
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Failed to fetch Outlook accounts',
        accounts: []
      };
    }
  }

  /**
   * Test the connection to Outlook
   */
  async testConnection() {
    try {
      const response = await this.makeBackendRequest('/test');
      return response;
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Failed to connect to Outlook'
      };
    }
  }
}

// Export instance instead of class
const outlookApiInstance = new OutlookApi();
export default outlookApiInstance;
